import { useEffect, useState } from 'react';
import type { PagedResult, ProductRecord } from '../types/index';
import { getProductTotalsAbortable } from './useProducts';

export default function useProductPageResults({
  organizationId,
  brandOptions,
  marketOptions,
  lookupsReady,
  refreshKey,
  actionNonce,
  page,
  pageSize,
  filters,
  sort,
}) {
  const [state, setState] = useState<{
    organizationId: unknown;
    results: PagedResult<ProductRecord>;
    isLoading: boolean;
  }>({
    organizationId,
    results: { page, pageSize, total: 0, data: [] },
    isLoading: Boolean(organizationId && lookupsReady),
  });

  useEffect(() => {
    let current = true;
    setState({
      organizationId,
      results: { page, pageSize, total: 0, data: [] },
      isLoading: Boolean(organizationId && lookupsReady),
    });

    if (!organizationId || !lookupsReady) return () => { current = false; };

    const brandNameById = Object.fromEntries(
      (brandOptions || []).map((brand) => [String(brand.id), brand.name || '']),
    );
    const marketNameById = Object.fromEntries(
      (marketOptions || []).map((market) => [String(market.id), market.name || market.code || '']),
    );
    const { promise, cancel } = getProductTotalsAbortable({
      organizationId,
      page,
      pageSize,
      ...filters,
      sortField: sort?.fieldName ?? null,
      sortAscending: sort?.isAscending ?? null,
      nameSortAscending: sort?.fieldName === 'name' ? sort.isAscending : null,
      lastModifiedSortAscending: sort?.fieldName === 'lastModified' ? sort.isAscending : null,
      brandNameById,
      marketNameById,
    });

    promise
      .then((results) => {
        if (!current) return;
        setState({
          organizationId,
          isLoading: false,
          results: {
            ...results,
            page,
            pageSize,
            data: (results?.data || []).map((product) => ({
              ...product,
              isArchived: typeof product?.isArchived === 'boolean'
                ? product.isArchived
                : product?.active === false,
              lastModified: !product.lastModified?.length
                ? null
                : new Date(product.lastModified).toLocaleDateString(),
            })),
          },
        });
      })
      .catch((error) => {
        if (!current || error?.name === 'AbortError' || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
        setState((value) => ({ ...value, isLoading: false }));
      });

    return () => {
      current = false;
      cancel();
    };
  }, [
    organizationId,
    lookupsReady,
    refreshKey,
    actionNonce,
    page,
    pageSize,
    filters.marketId,
    filters.brandId,
    filters.active,
    filters.name,
    sort,
    brandOptions,
    marketOptions,
  ]);

  if (state.organizationId !== organizationId) {
    return {
      results: { page: 0, pageSize, total: 0, data: [] } as PagedResult<ProductRecord>,
      isLoading: Boolean(organizationId && lookupsReady),
    };
  }

  return { results: state.results, isLoading: state.isLoading };
}
